import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import useAuthorizersService from "../../services/useAuthorizersService";
import useRequestersService from "../../services/useRequestersService";
import { ServiceEmailSenderOption } from "../../models/ServiceEmailRequestModel";
import showToast from "../../utils/toast";
import "../Services/Adicional/AdicionalCatalogModal.css";
import "./styles/EmailSenderQuickAddModal.css";

export type EmailSenderQuickCreated = {
  sender_ref: string;
  options: ServiceEmailSenderOption[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName?: string;
  onCreated: (result: EmailSenderQuickCreated) => void;
};

type FormState = {
  name: string;
  first_lastname: string;
  second_lastname: string;
  phone_number: string;
  email: string;
  as_requester: boolean;
  as_authorizer: boolean;
};

const emptyForm = (): FormState => ({
  name: "",
  first_lastname: "",
  second_lastname: "",
  phone_number: "",
  email: "",
  as_requester: true,
  as_authorizer: false,
});

const firstErrorMessage = (response: any, fallback: string) => {
  const errors = response?.errors ?? response?.data?.errors ?? response?.data;
  const firstFieldErr =
    typeof errors === "object" && errors
      ? (Object.values(errors).flat?.()?.[0] ?? Object.values(errors)[0])
      : null;
  if (typeof firstFieldErr === "string" && firstFieldErr.trim()) return firstFieldErr.trim();
  if (typeof response?.message === "string" && response.message.trim()) return response.message.trim();
  return fallback;
};

const buildLabel = (
  typeLabel: string,
  name: string,
  firstLastname: string,
  email: string,
  companyName?: string
) => {
  const fullName = `${name} ${firstLastname}`.trim();
  const context = companyName ? ` - ${companyName}` : "";
  return `${typeLabel}: ${email || fullName}${context}`;
};

/**
 * Alta rápida de emisor (autorizador, solicitante o ambos),
 * con el mismo criterio de roles que el formulario de pasajeros.
 */
const EmailSenderQuickAddModal: React.FC<Props> = ({
  isOpen,
  onClose,
  companyId,
  companyName,
  onCreated,
}) => {
  const { store: storeAuthorizer, fetching: authorizerFetching } = useAuthorizersService();
  const { store: storeRequester, fetching: requesterFetching } = useRequestersService();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const loading = saving || Boolean(authorizerFetching.store || requesterFetching.store);

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm());
      setSubmitted(false);
      setSaving(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && !loading) onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, loading]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const storeOne = (
    storeFn: (payload: any, events: any) => void,
    payload: Record<string, unknown>
  ) =>
    new Promise<any>((resolve, reject) => {
      storeFn(payload, {
        onSuccess: (response: any) => resolve(response),
        onError: (response: any) => reject(response),
      });
    });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);

    const name = form.name.trim();
    const firstLastname = form.first_lastname.trim();
    const secondLastname = form.second_lastname.trim();
    const phone = form.phone_number.trim();
    const email = form.email.trim();
    const asRequester = form.as_requester;
    const asAuthorizer = form.as_authorizer;

    if (!name || !firstLastname || !email || !companyId || loading) return;
    if (!asRequester && !asAuthorizer) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast("Ingresa un correo válido", "error");
      return;
    }

    const payload = {
      company: companyId,
      name,
      first_lastname: firstLastname,
      second_lastname: secondLastname,
      phone_number: phone,
      email,
    };

    setSaving(true);
    try {
      const options: ServiceEmailSenderOption[] = [];
      let senderRef = "";

      if (asRequester) {
        const response = await storeOne(storeRequester, payload);
        const id = response?.data?.id;
        if (typeof id !== "number") {
          throw new Error("No se pudo obtener el ID del solicitante creado");
        }
        const option: ServiceEmailSenderOption = {
          value: `REQUESTER:${id}`,
          id,
          type: "REQUESTER",
          type_label: "Solicitante",
          email,
          name: `${name} ${firstLastname}`.trim(),
          company_id: companyId,
          company_name: companyName,
          label: buildLabel("Solicitante", name, firstLastname, email, companyName),
        };
        options.push(option);
        senderRef = option.value;
      }

      if (asAuthorizer) {
        const response = await storeOne(storeAuthorizer, payload);
        const id = response?.data?.id;
        if (typeof id !== "number") {
          throw new Error("No se pudo obtener el ID del autorizador creado");
        }
        const option: ServiceEmailSenderOption = {
          value: `AUTHORIZER:${id}`,
          id,
          type: "AUTHORIZER",
          type_label: "Autorizador",
          email,
          name: `${name} ${firstLastname}`.trim(),
          company_id: companyId,
          company_name: companyName,
          label: buildLabel("Autorizador", name, firstLastname, email, companyName),
        };
        options.push(option);
        // Si solo se creó autorizador, o ambos: preferir el último rol marcado como emisor activo.
        // Con ambos, dejamos solicitante como emisor por defecto (más habitual en correos).
        if (!senderRef) senderRef = option.value;
      }

      const rolesLabel =
        asRequester && asAuthorizer
          ? "solicitante y autorizador"
          : asRequester
            ? "solicitante"
            : "autorizador";
      showToast(`Emisor creado correctamente (${rolesLabel})`, "success");
      onCreated({ sender_ref: senderRef, options });
    } catch (error: any) {
      showToast(firstErrorMessage(error, "No se pudo crear el emisor"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const emailInvalid = submitted && !form.email.trim();
  const rolesInvalid = submitted && !form.as_requester && !form.as_authorizer;

  return (
    <div className="adicional-modal-overlay" onClick={loading ? undefined : onClose}>
      <div
        className="adicional-modal-content email-sender-quick-add"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="adicional-modal-header">
          <h3 className="adicional-modal-title">Nuevo emisor</h3>
          <button
            type="button"
            className="adicional-modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="adicional-modal-form">
          <p className="email-sender-quick-add__hint">
            Crea un autorizador, un solicitante o ambos, con los mismos datos básicos del catálogo.
          </p>

          <div className="email-sender-quick-add__roles" role="group" aria-label="Roles">
            <label className="email-sender-quick-add__role">
              <input
                type="checkbox"
                checked={form.as_requester}
                onChange={(event) => updateField("as_requester", event.target.checked)}
                disabled={loading}
              />
              <span>Solicitante</span>
            </label>
            <label className="email-sender-quick-add__role">
              <input
                type="checkbox"
                checked={form.as_authorizer}
                onChange={(event) => updateField("as_authorizer", event.target.checked)}
                disabled={loading}
              />
              <span>Autorizador</span>
            </label>
          </div>
          {rolesInvalid && (
            <span className="email-sender-quick-add__error">
              Marca al menos un rol: solicitante, autorizador o ambos.
            </span>
          )}

          <div className="email-sender-quick-add__grid">
            <div className="adicional-modal-field">
              <label htmlFor="email-sender-name">Nombre</label>
              <input
                id="email-sender-name"
                type="text"
                value={form.name}
                onChange={(event) => updateField("name", event.target.value)}
                placeholder="Ej. Juan"
                required
                autoFocus
                disabled={loading}
                maxLength={150}
              />
            </div>
            <div className="adicional-modal-field">
              <label htmlFor="email-sender-first-lastname">Primer apellido</label>
              <input
                id="email-sender-first-lastname"
                type="text"
                value={form.first_lastname}
                onChange={(event) => updateField("first_lastname", event.target.value)}
                placeholder="Ej. Pérez"
                required
                disabled={loading}
                maxLength={150}
              />
            </div>
            <div className="adicional-modal-field">
              <label htmlFor="email-sender-second-lastname">Segundo apellido</label>
              <input
                id="email-sender-second-lastname"
                type="text"
                value={form.second_lastname}
                onChange={(event) => updateField("second_lastname", event.target.value)}
                placeholder="Opcional"
                disabled={loading}
                maxLength={150}
              />
            </div>
            <div className="adicional-modal-field">
              <label htmlFor="email-sender-phone">Celular</label>
              <input
                id="email-sender-phone"
                type="text"
                value={form.phone_number}
                onChange={(event) => updateField("phone_number", event.target.value)}
                placeholder="Ej. +56 9 1234 5678"
                disabled={loading}
                maxLength={20}
              />
            </div>
            <div className="adicional-modal-field email-sender-quick-add__field-full">
              <label htmlFor="email-sender-email">Correo</label>
              <input
                id="email-sender-email"
                type="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                placeholder="Ej. juan@empresa.cl"
                required
                disabled={loading}
                maxLength={255}
              />
              {emailInvalid && (
                <span className="email-sender-quick-add__error">El correo es obligatorio.</span>
              )}
            </div>
          </div>

          <div className="adicional-modal-actions">
            <button
              type="button"
              className="adicional-modal-btn secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="adicional-modal-btn primary"
              disabled={
                loading ||
                !form.name.trim() ||
                !form.first_lastname.trim() ||
                !form.email.trim() ||
                (!form.as_requester && !form.as_authorizer)
              }
            >
              {loading ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailSenderQuickAddModal;
